import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { resetCacheStamp } from "../../util/cacheManager";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { handleNetworkResponse } from "../../util/helper";
import { Area } from "../../types/Area";
import { Persister } from "../../util/persister";

// Define a service using a base URL and expected endpoints
export const areaApi = createApi({
  reducerPath: "areaApi",
  tagTypes: [CacheStoreKey.metaAreaDBStamp],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({
    // mutation api
    createArea: build.mutation({
      queryFn: async (area: Omit<Area, 'id'>) => {
        resetCacheStamp(CacheStoreKey.metaAreaDBStamp);
        const response = await axios.post(
          `/area/create`,
          area
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaAreaDBStamp],
    }),

    updateArea: build.mutation({
      queryFn: async (area: Area) => {
        resetCacheStamp(CacheStoreKey.metaAreaDBStamp);
        const response = await axios.post(
          `/area/update`,
          area
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaAreaDBStamp],
    }),

    deleteArea: build.mutation({
      queryFn: async (id: number) => {
        resetCacheStamp(CacheStoreKey.metaAreaDBStamp);
        const response = await axios.post(`/area/delete/${id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaAreaDBStamp],
    }),

    // read api
    readAreas: build.query({
      queryFn: async () => {
        const response =  await Persister.requestData(CacheStoreKey.metaAreaDBStamp, 'GET', `/areas`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.metaAreaDBStamp],
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateAreaMutation,
  useDeleteAreaMutation,
  useUpdateAreaMutation,
  useLazyReadAreasQuery,
  useReadAreasQuery
} = areaApi;

export type AreaApiType = typeof areaApi;
